import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import {
  approveFamilyChangeRequest,
  getFamilyChangeRequests,
  rejectFamilyChangeRequest,
} from "../api/api";

const statusVariant = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

function mediaUrl(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `http://localhost:8000${path}`;
}

export default function FamilyRequestsAdmin() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviewTarget, setReviewTarget] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [payloadText, setPayloadText] = useState("{}");
  const [saving, setSaving] = useState(false);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getFamilyChangeRequests();
      setRequests(res.data || []);
    } catch (e) {
      setError("Impossible de charger les demandes famille.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const submitDecision = async (decision) => {
    if (!reviewTarget) return;
    setSaving(true);
    try {
      if (decision === "approved") {
        let payload = undefined;
        if (payloadText.trim()) {
          payload = JSON.parse(payloadText);
        }
        await approveFamilyChangeRequest(reviewTarget.id, adminNote, payload);
      } else {
        await rejectFamilyChangeRequest(reviewTarget.id, adminNote);
      }
      setReviewTarget(null);
      setAdminNote("");
      setPayloadText("{}");
      await loadRequests();
      window.dispatchEvent(new Event("amicale:notifications-refresh"));
    } catch (e) {
      const data = e?.response?.data;
      const detail =
        typeof data === "string"
          ? data
          : data?.detail || data?.non_field_errors?.[0] || JSON.stringify(data || {});
      setError(e instanceof SyntaxError ? "Payload JSON invalide." : detail || "Decision impossible a enregistrer.");
    } finally {
      setSaving(false);
    }
  };

  const openReview = (item) => {
    setReviewTarget(item);
    setAdminNote("");
    setPayloadText(JSON.stringify(item.payload || {}, null, 2));
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Demandes famille</h4>
          <p className="text-muted mb-0">Corrections demandees par les adherents sur parents, enfants et conjoint.</p>
        </div>
        <Button variant="outline-primary" onClick={loadRequests}>Actualiser</Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Adherent</th>
                  <th>Cible</th>
                  <th>Action</th>
                  <th>Message</th>
                  <th>Statut</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">Aucune demande.</td>
                  </tr>
                ) : (
                  requests.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.adherent_full_name}</strong>
                        <div className="text-muted small">{item.adherent_matricule}</div>
                      </td>
                      <td>{item.target_type} #{item.target_id || "-"}</td>
                      <td>{item.action}</td>
                      <td style={{ maxWidth: 360 }}>{item.message || "-"}</td>
                      <td>
                        <Badge bg={statusVariant[item.status] || "secondary"}>{item.status}</Badge>
                      </td>
                      <td>{item.created_at ? new Date(item.created_at).toLocaleString() : "-"}</td>
                      <td className="text-end">
                        {item.status === "pending" && (
                          <Button size="sm" onClick={() => openReview(item)}>
                            Traiter
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!reviewTarget} onHide={() => setReviewTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Traiter la demande</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="light">
            Le payload JSON sera applique automatiquement si les champs correspondent a la cible famille.
          </Alert>
          {reviewTarget && (
            <div className="mb-3">
              <strong>{reviewTarget.target_type} #{reviewTarget.target_id || "-"}</strong>
              <div className="text-muted small">{reviewTarget.message || "Aucun message adherent."}</div>
            </div>
          )}
          {reviewTarget?.proof_files?.length > 0 && (
            <div className="mb-3">
              <Form.Label>Pieces justificatives</Form.Label>
              {reviewTarget.proof_files.map((file) => (
                <div key={file.id} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
                  <span>{file.label || `Piece #${file.id}`}</span>
                  <Button size="sm" href={mediaUrl(file.file)} target="_blank" rel="noreferrer">
                    Ouvrir
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Payload structure</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              spellCheck={false}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Note admin</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Commentaire visible par l'adherent"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReviewTarget(null)}>Annuler</Button>
          <Button variant="danger" disabled={saving} onClick={() => submitDecision("rejected")}>Refuser</Button>
          <Button variant="success" disabled={saving} onClick={() => submitDecision("approved")}>Approuver</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
